import { defineStore, acceptHMRUpdate } from "pinia";
import {
  AdminClient,
  LinkClient,
  ClientDTO,
  GetVideoDTO,
  PortalDTO,
  RoleTypeDTO,
  SavePortalCommand,
  SaveVideoRequest,
  SetNewPasswordDTO,
  VideoListItemDTO,
  SearchLinkDTO,
  SaveClientCommand,
  WarningLinkDTO,
  SaveStaffNotificationRequest,
  ResolveStaffNotificationsRequest,
  CountsDTO,
  StaffNoteDTO,
  SaveStaffNoteRequest,
  GetStaffNotificationsVm,
  GetStaffNotificationsRequest,
  MovePortalToClientRequest,
  AdminNoteDTO,
  SaveAdminNoteRequest,
  OriginationNumber,
  MetricsClient,
  PortalMetricsVM,
  TopPortalsVM,
  Setting,
  SaveEmailTemplate,
} from "./generatedFetchClient";

let adminClient = new AdminClient();
let linkClient = new LinkClient();
let metricsClient = new MetricsClient();

export const useAdminStore = defineStore({
  id: "adminStore",
  state() {
    return {
      clients: [] as ClientDTO[],
      roleTypes: [] as RoleTypeDTO[],
      videos: [] as VideoListItemDTO[],
      selectedVideo: null as GetVideoDTO | null,
      selectedClient: null as ClientDTO | null,
      selectedClientPortalList: [] as PortalDTO[],

      staffNotificationVm: null as GetStaffNotificationsVm | null,
      staffNotificationSearchString: "",

      counts: null as CountsDTO | null,
      topPortals: null as TopPortalsVM | null,
      topPortalsCache: {} as Record<string, TopPortalsVM>,

      staffUserSetupAfterLogin: false,
      originationNumbers: [] as OriginationNumber[],
      emailTemplates: [] as Setting[],
    };
  },
  actions: {
    async setClients(
      _adminClient?: AdminClient,
      _linkClient?: LinkClient,
      _metricsClient?: MetricsClient
    ) {
      adminClient = _adminClient!;
      linkClient = _linkClient!;
      metricsClient = _metricsClient!;
    },
    async ensureStaffAdminStartup() {
      //double check that stuff is loaded to make Staff admin work

      if (this.clients?.length <= 0) await this.getClientList();

      if (this.originationNumbers?.length <= 0) {
        var startupData = await adminClient.getStartupData();
        this.originationNumbers = startupData.originationNumbers ?? [];
        console.log("Got startup data", startupData);
      }
    },
    setAuthToken(token: string) {
      adminClient.setAuthToken(token);
      linkClient.setAuthToken(token);
      metricsClient.setAuthToken(token);
    },
    async getVideosList(force: boolean = false): Promise<VideoListItemDTO[] | string> {
      try {
        if (this.videos.length == 0 || force) {
          this.videos = (await adminClient.getVideosList()).sort((a, b) =>
            (a.fileName ?? '').toLowerCase() > (b.fileName ?? '').toLowerCase() ? 1 : -1
          );
        }
        return this.videos;
      } catch (apiError: any) {
        console.error("error loading videos", apiError);
        return apiError?.response?.data;
      }
    },
    async getVideo(videoId: number): Promise<GetVideoDTO | string> {
      try {
        let video = await adminClient.getVideo(videoId);
        if (video) {
          this.selectedVideo = video;
        }
        return "";
      } catch (error) {
        let msg = `error getting video with id: ${videoId} ` + error;
        console.error(msg);
        return msg;
      }
    },
    async saveVideo(video: SaveVideoRequest): Promise<string> {
      try {
        console.log("Saving video", video);
        let number = await adminClient.saveVideo(video);
        return number > 0 ? "" : "Error saving video";
      } catch (apiError: any) {
        return apiError?.response?.toString() ?? apiError?.message;
      }
    },
    async deleteVideo(videoId: number): Promise<string> {
      try {
        await adminClient.deleteVideo(videoId);
        return "";
      } catch (apiError: any) {
        console.error("Error deleting video", apiError);
        return apiError.response;
      }
    },
    async getPortalsByClient(clientId: number): Promise<string> {
      try {
        let portalList = await adminClient.getPortalsByClient(clientId);
        if (portalList) {
          this.selectedClientPortalList = portalList;
        }
        return "";
      } catch (error: any) {
        let msg = `error getting portals for client with id: ${clientId}` + error;
        console.error(msg);
        return msg;
      }
    },
    async savePortal(savePortal: SavePortalCommand): Promise<string> {
      try {
        console.log("Saving portal", savePortal);
        let number = await adminClient.savePortal(savePortal);
        if (number > 0) {
          return ""; // no error
        } else {
          let error = "Problem saving portal";
          console.warn(error);
          return error;
        }
      } catch (apiError: any) {
        console.error("Error saving portal", apiError);
        return apiError.response;
      }
    },
    async deletePortal(portalId: number): Promise<string> {
      try {
        await adminClient.deletePortal(portalId);
        return "";
      } catch (apiError: any) {
        console.error("Error deleting portal", apiError);
        return apiError.response;
      }
    },
    async getClientList(): Promise<ClientDTO[] | string> {
      try {
        let response = await adminClient.getClients();
        if (response && response.clients) {
          this.clients = response.clients as ClientDTO[];
          return this.clients;
        }
      } catch (apiError: any) {
        console.error("error loading clients", apiError);
        return apiError?.response?.data;
      }
      return "Not found";
    },
    async getClient(clientId: number): Promise<string> {
      try {
        let client = await adminClient.getClient(clientId);
        if (client) {
          this.selectedClient = client;
        }
        return "";
      } catch (error: any) {
        let msg = `error getting client with id: ${clientId}` + error;
        console.error(msg);
        return msg;
      }
    },
    async saveClient(saveClient: SaveClientCommand): Promise<string> {
      try {
        console.log("Saving client", saveClient);
        let number = await adminClient.saveClient(saveClient);
        if (number > 0) {
          await this.getClientList();
          return ""; // no error
        } else {
          let error = "Problem saving client";
          console.warn(error);
          return error;
        }
      } catch (apiError: any) {
        console.error("Error saving client", apiError);
        return apiError.response;
      }
    },
    async getRoleTypes(force: boolean = false): Promise<RoleTypeDTO[] | string> {
      try {
        if (this.roleTypes?.length > 0 && !force) return this.roleTypes;

        let rolesVm = await adminClient.getRoleTypes();
        if (rolesVm && rolesVm.roleTypes) {
          this.roleTypes = rolesVm.roleTypes;
          return this.roleTypes;
        }
        return "There was a problem loading roles";
      } catch (apiError: any) {
        console.error("error loading roleTypes", apiError);
        return apiError?.response?.data?.toString();
      }
    },
    async saveRoleType(saveRole: RoleTypeDTO): Promise<string> {
      try {
        let id = await adminClient.saveRoleType(saveRole);
        if (id > 0) {
          await this.getRoleTypes(true);
          return ""; // no error
        }
        return "There was a problem saving role";
      } catch (apiError: any) {
        console.error("Error saving client", apiError);
        return apiError.response;
      }
    },
    async deleteRoleType(roleTypeId: number): Promise<string> {
      try {
        await adminClient.deleteRoleType(roleTypeId);
        return "";
      } catch (apiError: any) {
        console.error("Error deleting RoleType", apiError);
        return apiError.response;
      }
    },
    async setNewPassword(savePassword: SetNewPasswordDTO): Promise<string> {
      try {
        let response = await adminClient.setNewPassword(savePassword);
        if (response && response.status === 200) return "";

        return "There was a problem setting new password";
      } catch (apiError: any) {
        let message = "Error setting new password " + apiError;
        console.error(message);
        return message;
      }
    },
    async searchLinks(search: string): Promise<SearchLinkDTO[] | string> {
      try {
        let response = await linkClient.searchLinks(search);
        if (response) return response;

        return "There was a problem searching links";
      } catch (apiError: any) {
        let message = "Error searching for links" + apiError;
        console.error(message);
        return message;
      }
    },
    async getLinkWarnings(): Promise<WarningLinkDTO[] | string> {
      try {
        let response = await adminClient.getLinkWarnings();
        if (response) return response;

        return "There was a problem searching links";
      } catch (apiError: any) {
        let message = "Error searching for links" + apiError;
        console.error(message);
        return message;
      }
    },
    async suppressWarningsByUrl(url: string): Promise<string> {
      try {
        let response = await adminClient.suppressWarningsByUrl(url);
        if (response) return "";

        return "There was a problem suppressing link warnings";
      } catch (apiError: any) {
        let message = "Error suppressing link warnings " + apiError;
        console.error(message);
        return message;
      }
    },
    async getStaffNotifications(
      request: GetStaffNotificationsRequest,
      force: boolean = false
    ): Promise<string> {
      try {
        if (this.staffNotificationVm === null || force)
          this.staffNotificationVm = await adminClient.getStaffNotifications(request);
        return "";
      } catch (apiError: any) {
        console.error("Error loading staff notifications", apiError);
        return apiError.response;
      }
    },
    async saveStaffNotification(request: SaveStaffNotificationRequest): Promise<string> {
      try {
        await adminClient.saveStaffNotification(request);
        return "";
      } catch (apiError: any) {
        console.error("Error saving staff notification", apiError);
        return apiError.response;
      }
    },
    async resolveStaffNotifications(request: ResolveStaffNotificationsRequest): Promise<string> {
      try {
        await adminClient.resolveStaffNotifications(request);
        return "";
      } catch (apiError: any) {
        console.error("Error resolving staff notification", apiError);
        return apiError.response;
      }
    },
    async getCounts(force: boolean = false): Promise<string> {
      try {
        if (this.counts == null || force) this.counts = await adminClient.getCounts();
        return "";
      } catch (apiError: any) {
        console.error("Error loading counts", apiError);
        return "Error loading counts";
      }
    },
    async getStaffNote(page: string): Promise<StaffNoteDTO> {
      try {
        return await adminClient.getStaffNote(page);
      } catch (apiError: any) {
        console.error("Error getting staff note for " + page, apiError);
        return apiError.response;
      }
    },
    async saveStaffNote(request: SaveStaffNoteRequest): Promise<string> {
      try {
        await adminClient.saveStaffNote(request);
        return "";
      } catch (apiError: any) {
        console.error("Error saving staff note", apiError);
        return apiError.response;
      }
    },
    async getAdminNote(
      page: string,
      isForTenantAdmin: boolean,
      portalId: number
    ): Promise<AdminNoteDTO> {
      try {
        return await adminClient.getAdminNote(page, isForTenantAdmin, portalId);
      } catch (apiError: any) {
        console.error("Error getting staff note for " + page, apiError);
        return apiError.response;
      }
    },
    async saveAdminNote(request: SaveAdminNoteRequest): Promise<string> {
      try {
        await adminClient.saveAdminNote(request);
        return "";
      } catch (apiError: any) {
        console.error("Error saving admin note", apiError);
        return apiError.response;
      }
    },
    async movePortalToClient(request: MovePortalToClientRequest): Promise<string> {
      try {
        await adminClient.movePortalToClient(request);
        return "";
      } catch (apiError: any) {
        console.error("Error moving portal to client", apiError);
        return apiError.response;
      }
    },
    async clearCache(): Promise<string> {
      try {
        await adminClient.clearCache();
        return "";
      } catch (apiError: any) {
        console.error("Error clearing cache ", apiError);
        return apiError.response;
      }
    },
    async generatePasswordResetLink(email: string, siteCode: string): Promise<string> {
      try {
        return await adminClient.generatePasswordResetLink(email, siteCode);
      } catch (apiError: any) {
        console.error("Error generating password reset link", apiError);
        return apiError.response;
      }
    },
    async getTopPortals(days?: number, top: number = 5): Promise<string> {
      const cacheKey = `${days ?? "all"}_${top}`;
      if (this.topPortalsCache[cacheKey]) {
        this.topPortals = this.topPortalsCache[cacheKey];
        return "";
      }
      try {
        const result = await metricsClient.getTopPortals(days, top);
        this.topPortals = result;
        this.topPortalsCache[cacheKey] = result;
        return "";
      } catch (apiError: any) {
        console.error("Error getting top portals", apiError);
        return apiError?.response ? apiError.response : apiError.toString();
      }
    },
    async getPortalMetrics(): Promise<PortalMetricsVM> {
      try {
        return await metricsClient.getPortalMetrics();
      } catch (apiError: any) {
        console.error("Error getting portal metrics", apiError);
        return apiError.response;
      }
    },
    async getEmailTemplates(): Promise<Setting[] | string> {
      try {
        return await adminClient.getEmailTemplates();
      } catch (apiError: any) {
        console.error("Error getting email templates", apiError);
        return apiError.response;
      }
    },
    async saveEmailTemplate(template: SaveEmailTemplate): Promise<string> {
      try {
        await adminClient.saveEmailTemplate(template);
        return "";
      } catch (apiError: any) {
        console.error("Error saving email template", apiError);
        return apiError.response;
      }
    },
    async deleteEmailTemplate(
      settingId: number
    ): Promise<string> {
      try {
        await adminClient.deleteEmailTemplate(settingId);
        return "";
      } catch (apiError: any) {
        console.error("Error deleting email template", apiError);
        return apiError.response;
      }
    },
  },
  getters: {},
});

//Enable Hot Module Replacement
// https://pinia.esm.dev/cookbook/hot-module-replacement.html
if ((import.meta as any).hot) {
  (import.meta as any).hot.accept(acceptHMRUpdate(useAdminStore as any, (import.meta as any).hot));
}
